import { useToken } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

export const totpCountdownRingSize = 32;

const radius = 13;
const strokeWidth = 3;
const circumference = 2 * Math.PI * radius;
const tickMs = 100;

type Props = {
  generatedAt: string;
  lifetimeSeconds: number;
};

export const TotpCountdownRing = ({ generatedAt, lifetimeSeconds }: Props) => {
  const [trackColor, indicatorColor] = useToken('colors', ['gray.200', 'blue.500']);
  const [remainingFraction, setRemainingFraction] = useState(1);
  const [secondsLeft, setSecondsLeft] = useState(lifetimeSeconds);

  useEffect(() => {
    const expiresAtMs = new Date(generatedAt).getTime() + lifetimeSeconds * 1000;

    function tick() {
      const remainingMs = expiresAtMs - Date.now();
      const fraction = remainingMs / (lifetimeSeconds * 1000);
      setRemainingFraction(Math.max(0, Math.min(1, fraction)));
      setSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
    }

    tick();
    const interval = setInterval(tick, tickMs);
    return () => clearInterval(interval);
  }, [generatedAt, lifetimeSeconds]);

  const center = totpCountdownRingSize / 2;

  return (
    <svg width={totpCountdownRingSize} height={totpCountdownRingSize} viewBox={`0 0 ${totpCountdownRingSize} ${totpCountdownRingSize}`} style={{ flexShrink: 0 }}>
      <g style={{ transform: 'scaleX(-1) rotate(-90deg)', transformOrigin: 'center' }}>
        <circle cx={center} cy={center} r={radius} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={indicatorColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - remainingFraction)}
          style={{ transition: `stroke-dashoffset ${tickMs}ms linear` }}
        />
      </g>
      <text x={center} y={center} textAnchor="middle" dominantBaseline="central" fontSize={10} fill="currentColor">
        {secondsLeft}
      </text>
    </svg>
  );
};
